import { useMemo } from "react";
import { useQuery } from "react-query";
import {
	fetchAllAssetLatestPrice,
	fetchAssets,
	fetchDaily,
	fetchDividends,
	fetchInvestments,
	fetchInvestmentSummary,
} from "../../utilities/api";
import { useCurrency } from "../dropdown";

// Market data is polled while the dashboard is open; a stale quote is worse
// than a request.
const MARKET_QUERY_OPTIONS = {
	refetchInterval: 1000 * 60 * 5,
	refetchOnMount: true,
};

export const useCurrencyCode = () => {
	const { currency } = useCurrency();
	return currency?.name || "USD";
};

// Every money figure now arrives from the API already converted into the
// display currency, so the currency code is part of the query key: switching
// currency refetches rather than re-multiplying a cached USD number. Routing
// every view through these hooks is what keeps that safe -- a view that called
// useQuery("fetchDaily") directly would keep serving the previously selected
// currency's cache entry under the new currency's symbol.
export const useInvestmentsQuery = (options) => {
	const code = useCurrencyCode();
	return useQuery(["fetchInvestments", code], () => fetchInvestments(code), {
		...MARKET_QUERY_OPTIONS,
		...options,
	});
};

export const useDailyQuery = (options) => {
	const code = useCurrencyCode();
	return useQuery(["fetchDaily", code], () => fetchDaily(code), {
		...MARKET_QUERY_OPTIONS,
		...options,
	});
};

export const useDividendsQuery = (options) => {
	const code = useCurrencyCode();
	return useQuery(["fetchDividends", code], () => fetchDividends(code), {
		...MARKET_QUERY_OPTIONS,
		...options,
	});
};

export const useLatestPricesQuery = (options) => {
	const code = useCurrencyCode();
	return useQuery(
		["fetchAllAssetLatestPrice", code],
		() => fetchAllAssetLatestPrice(code),
		{ ...MARKET_QUERY_OPTIONS, ...options }
	);
};

export const useInvestmentSummaryQuery = (asOfDate, options) => {
	const code = useCurrencyCode();
	return useQuery(
		["fetchInvestmentSummary", code, asOfDate || null],
		() => fetchInvestmentSummary(asOfDate, code),
		{ ...MARKET_QUERY_OPTIONS, ...options }
	);
};

const YEAR_START_MONTH_INDEX = 11;
const YEAR_START_DAY = 12;
const YEAR_END_DAY = 11;

// Categorical palette validated for colour-vision deficiency in this slot order
// (adjacent CVD ΔE >= 9). Slots are handed out in a fixed order and never cycled:
// a ninth asset shares the neutral "other" swatch instead of reusing a hue.
const ASSET_SWATCHES = [
	{ dot: "bg-[#2a78d6]", line: "#2a78d6" },
	{ dot: "bg-[#eb6834]", line: "#eb6834" },
	{ dot: "bg-[#1baf7a]", line: "#1baf7a" },
	{ dot: "bg-[#eda100]", line: "#eda100" },
	{ dot: "bg-[#e87ba4]", line: "#e87ba4" },
	{ dot: "bg-[#008300]", line: "#008300" },
	{ dot: "bg-[#4a3aa7]", line: "#4a3aa7" },
	{ dot: "bg-[#e34948]", line: "#e34948" },
];

const OTHER_SWATCH = { dot: "bg-[#a3a29c]", line: "#a3a29c", isOther: true };

const DAY_IN_MS = 1000 * 60 * 60 * 24;

const toDate = (value) => {
	if (!value) return null;
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime()) ? null : date;
};

export const normalizeAsset = (asset) => String(asset || "").trim().toLowerCase();

export const normalizeDateKey = (value) => {
	const date = toDate(value);
	return date ? date.toISOString().slice(0, 10) : null;
};

export const humanizeAssetLabel = (asset) => {
	const normalized = normalizeAsset(asset);
	if (!normalized) return "Unknown";

	if (normalized.length <= 6) {
		return normalized.toUpperCase();
	}

	return normalized
		.split(/[^a-z0-9]+/i)
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
};

// Assets in the order they first entered the portfolio. A new asset appends to
// the end, so existing assets never change colour when one is added.
export const buildAssetOrder = (investments = []) => {
	const firstSeen = new Map();
	(investments || []).forEach((investment, index) => {
		const asset = normalizeAsset(investment?.asset);
		const date = getEffectiveTransactionDate(investment);
		if (!asset || !date) return;
		const current = firstSeen.get(asset);
		if (!current || date < current.date) firstSeen.set(asset, { date, index });
	});
	return Array.from(firstSeen.entries())
		.sort(([, left], [, right]) => left.date - right.date || left.index - right.index)
		.map(([asset]) => asset);
};

// Colour lookup keyed by asset identity (not rank), shared by every view so an
// asset wears the same colour in the chart, holdings, allocation and ledger.
export const useAssetPalette = () => {
	const { data: investments } = useInvestmentsQuery();
	return useMemo(() => {
		const slots = new Map(buildAssetOrder(investments).map((asset, index) => [asset, index]));
		return (asset) => {
			const slot = slots.get(normalizeAsset(asset));
			return slot !== undefined && slot < ASSET_SWATCHES.length
				? ASSET_SWATCHES[slot]
				: OTHER_SWATCH;
		};
	}, [investments]);
};

export const formatCurrency = (baseValue, currency, options = {}) => {
	const amount = Number(baseValue || 0);
	const currencyCode = currency?.name || "USD";

	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currencyCode,
			currencyDisplay: "symbol",
			maximumFractionDigits: 2,
			...options,
		}).format(amount);
	} catch {
		return `${currencyCode} ${amount.toFixed(2)}`;
	}
};

// Axis ticks and dense labels: $164.6K rather than $164,595.90.
export const formatCompactCurrency = (baseValue, currency) => {
	const amount = Number(baseValue || 0);
	const currencyCode = currency?.name || "USD";

	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currencyCode,
			currencyDisplay: "symbol",
			notation: "compact",
			maximumFractionDigits: 1,
		}).format(amount);
	} catch {
		return `${currencyCode} ${amount.toFixed(0)}`;
	}
};

export const formatSignedCurrency = (baseValue, currency) => {
	const amount = Number(baseValue || 0);
	const formatted = formatCurrency(Math.abs(amount), currency);
	if (Math.abs(amount) < 0.005) return formatted;
	return `${amount > 0 ? "+" : "−"}${formatted}`;
};

export const formatSignedPercent = (value, digits = 2) => {
	const amount = Number(value || 0);
	if (Math.abs(amount) < 0.5 * 10 ** -digits) return `${(0).toFixed(digits)}%`;
	return `${amount > 0 ? "+" : "−"}${Math.abs(amount).toFixed(digits)}%`;
};

export const getPl = (current, spent) => {
	const safeCurrent = Number(current || 0);
	const safeSpent = Number(spent || 0);
	if (safeSpent === 0) return [0, 0];
	const pl = safeCurrent - safeSpent;
	return [pl, (pl * 100) / safeSpent];
};

// Single source of truth for the annualised-return window. Previously repeated
// as an inline `new Date("2024-12-12")` in Performance.jsx and Overview.jsx.
export const PORTFOLIO_START_DATE = new Date("2024-12-12");

// Compound annual growth rate: the yearly rate that, compounding, turns the
// starting capital into today's value. Previously this scaled the total return
// linearly (`percent / years`), which overstates the rate -- 44.19% over 1.68
// years reads as 26.36% simple but only 24.39% compounded, and the gap widens
// the further a projection runs. Goal projections compound, so the rate feeding
// them has to be compounded too.
export const calculateAnnualInterest = (percent, startDate) => {
	const start = toDate(startDate);
	if (!start) return "0.00";

	const today = new Date();
	const daysElapsed = (today.getTime() - start.getTime()) / DAY_IN_MS;
	if (daysElapsed <= 0) return "0.00";

	const years = daysElapsed / 365;
	const growth = 1 + Number(percent || 0) / 100;
	// Wiped out (or worse): the compounded rate is a total annual loss.
	if (growth <= 0) return "-100.00";

	return ((Math.pow(growth, 1 / years) - 1) * 100).toFixed(2);
};

export const getEffectiveTransactionDate = (item) => {
	const createdAt = toDate(item?.createdAt);
	const transactionDate = toDate(item?.transactionDate);

	if (createdAt && transactionDate) {
		return createdAt <= transactionDate ? createdAt : transactionDate;
	}

	return createdAt || transactionDate || toDate(item?.date);
};

export const getSignedUnits = (investment) => {
	const units = Number(investment?.units || 0);
	return String(investment?.transactionType || "BUY").toUpperCase() === "SELL"
		? -units
		: units;
};

export const getSignedCashFlow = (investment) => {
	const amount = Number(investment?.value || 0) * Number(investment?.units || 0);
	return String(investment?.transactionType || "BUY").toUpperCase() === "SELL"
		? -amount
		: amount;
};

const sortByDate = (left, right) =>
	toDate(left)?.getTime() - toDate(right)?.getTime();

const buildPriceHistory = (daily = []) => {
	const history = new Map();

	daily.forEach((row) => {
		const asset = normalizeAsset(row?.asset);
		const dateKey = normalizeDateKey(row?.createdAt);
		if (!asset || !dateKey) return;

		const series = history.get(asset) || [];
		series.push({
			date: dateKey,
			price: Number(row?.value || 0),
		});
		history.set(asset, series);
	});

	for (const [asset, rows] of history.entries()) {
		const deduped = new Map();
		rows
			.sort((a, b) => sortByDate(a.date, b.date))
			.forEach((row) => deduped.set(row.date, row.price));
		history.set(
			asset,
			Array.from(deduped.entries()).map(([date, price]) => ({ date, price }))
		);
	}

	return history;
};

const getPriceOnOrBefore = (assetHistory, asOfDateKey) => {
	if (!assetHistory?.length) return 0;
	if (!asOfDateKey) {
		return Number(assetHistory[assetHistory.length - 1]?.price || 0);
	}

	let latest = 0;
	for (const row of assetHistory) {
		if (row.date > asOfDateKey) break;
		latest = Number(row.price || 0);
	}
	return latest;
};

export const buildPortfolioSnapshot = ({ investments = [], daily = [], asOfDate } = {}) => {
	const asOfDateKey = normalizeDateKey(asOfDate);
	const holdings = new Map();
	const values = new Map();
	const history = buildPriceHistory(daily);

	investments.forEach((investment) => {
		const transactionDate = getEffectiveTransactionDate(investment);
		const transactionDateKey = normalizeDateKey(transactionDate);
		const asset = normalizeAsset(investment?.asset);
		if (!asset || !transactionDateKey) return;
		if (asOfDateKey && transactionDateKey > asOfDateKey) return;

		holdings.set(asset, (holdings.get(asset) || 0) + getSignedUnits(investment));
	});

	let worth = 0;
	for (const [asset, units] of holdings.entries()) {
		const latestPrice = getPriceOnOrBefore(history.get(asset), asOfDateKey);
		const value = units * latestPrice;
		values.set(asset, value);
		worth += value;
	}

	return {
		worth,
		holdings,
		values,
	};
};

const emptyPortfolioSnapshot = () => ({
	worth: 0,
	holdings: new Map(),
	values: new Map(),
});

const buildLatestPriceMap = (rows = []) => {
	const priceMap = new Map();
	rows.forEach((row) => {
		const asset = normalizeAsset(row?.asset);
		if (!asset) return;
		priceMap.set(asset, Number(row?.value || 0));
	});
	return priceMap;
};

export const getInvestmentYearLabel = (value) => {
	const date = toDate(value);
	if (!date) return null;
	const month = date.getUTCMonth();
	const day = date.getUTCDate();
	return month > YEAR_START_MONTH_INDEX ||
		(month === YEAR_START_MONTH_INDEX && day >= YEAR_START_DAY)
		? date.getUTCFullYear() + 1
		: date.getUTCFullYear();
};

export const getInvestmentYearStart = (yearLabel) =>
	new Date(Date.UTC(yearLabel - 1, YEAR_START_MONTH_INDEX, YEAR_START_DAY, 0, 0, 0, 0));

export const getInvestmentYearEnd = (yearLabel) =>
	new Date(Date.UTC(yearLabel, YEAR_START_MONTH_INDEX, YEAR_END_DAY, 23, 59, 59, 999));

export const getPreviousYearEnd = (yearLabel) =>
	new Date(Date.UTC(yearLabel - 1, YEAR_START_MONTH_INDEX, YEAR_END_DAY, 23, 59, 59, 999));

export const buildInvestmentYears = ({
	investments = [],
	daily = [],
	latestPrices = [],
	dividends = [],
	now = new Date(),
} = {}) => {
	const orderedInvestments = [...investments]
		.map((investment) => ({
			...investment,
			_effectiveDate: getEffectiveTransactionDate(investment),
		}))
		.filter((investment) => investment._effectiveDate)
		.sort((left, right) => left._effectiveDate - right._effectiveDate);

	if (!orderedInvestments.length) return [];

	const firstYear = getInvestmentYearLabel(orderedInvestments[0]._effectiveDate);
	const currentYear = getInvestmentYearLabel(now);
	const currentDateKey = normalizeDateKey(now);
	const rows = [];
	const latestPriceMap = buildLatestPriceMap(latestPrices);

	for (let year = firstYear; year <= currentYear; year += 1) {
		const yearStart = getInvestmentYearStart(year);
		const yearEnd = getInvestmentYearEnd(year);
		const previousYearEnd = getPreviousYearEnd(year);
		const periodEnd = yearEnd < now ? yearEnd : now;
		const periodEndKey = normalizeDateKey(periodEnd);
		const baselineSnapshot =
			year === firstYear
				? emptyPortfolioSnapshot()
				: buildPortfolioSnapshot({ investments, daily, asOfDate: previousYearEnd });
		const baselineWorth = baselineSnapshot.worth;

		const yearStartKey = normalizeDateKey(yearStart);
		const assetCapital = new Map();
		const assetUnits = new Map();
		const newCapital = orderedInvestments.reduce((total, investment) => {
			const dateKey = normalizeDateKey(investment._effectiveDate);
			if (!dateKey || dateKey < yearStartKey || dateKey > periodEndKey) {
				return total;
			}
			const asset = normalizeAsset(investment?.asset);
			const cashFlow = getSignedCashFlow(investment);
			if (asset) {
				assetCapital.set(asset, (assetCapital.get(asset) || 0) + cashFlow);
				assetUnits.set(asset, (assetUnits.get(asset) || 0) + getSignedUnits(investment));
			}
			return total + cashFlow;
		}, 0);

		const assetDividends = new Map();
		const dividendIncome = dividends.reduce((total, dividend) => {
			const dateKey = normalizeDateKey(dividend?.date);
			if (!dateKey || dateKey < yearStartKey || dateKey > periodEndKey) {
				return total;
			}
			const asset = normalizeAsset(dividend?.asset);
			const amount = Number(dividend?.amount || 0);
			if (asset) {
				assetDividends.set(asset, (assetDividends.get(asset) || 0) + amount);
			}
			return total + amount;
		}, 0);

		const finalSnapshot = buildPortfolioSnapshot({
			investments,
			daily,
			asOfDate: periodEnd,
		});
		let finalWorth = finalSnapshot.worth;
		let finalValues = finalSnapshot.values;

		if (year === currentYear && latestPriceMap.size > 0) {
			finalValues = new Map(
				Array.from(finalSnapshot.holdings.entries()).map(([asset, units]) => [
					asset,
					units * (latestPriceMap.get(asset) || 0),
				])
			);
			finalWorth = Array.from(finalValues.values()).reduce(
				(total, value) => total + value,
				0
			);
		}

		const amountInvested = baselineWorth + newCapital;
		const assetSet = new Set([
			...baselineSnapshot.values.keys(),
			...assetCapital.keys(),
			...assetDividends.keys(),
			...finalSnapshot.holdings.keys(),
		]);
		const assets = Array.from(assetSet)
			.map((asset) => {
				const startingValue = baselineSnapshot.values.get(asset) || 0;
				const invested = assetCapital.get(asset) || 0;
				const endingValue = finalValues.get(asset) || 0;
				const dividendIncomeForAsset = assetDividends.get(asset) || 0;
				const amountInvestedForAsset = startingValue + invested;
				const profitLoss =
					endingValue - amountInvestedForAsset + dividendIncomeForAsset;
				const profitLossPercent =
					amountInvestedForAsset > 0
						? (profitLoss / amountInvestedForAsset) * 100
						: 0;

				return {
					asset,
					label: humanizeAssetLabel(asset),
					startingValue,
					invested,
					amountInvested: amountInvestedForAsset,
					endingValue,
					dividendIncome: dividendIncomeForAsset,
					profitLoss,
					profitLossPercent,
					startingUnits: baselineSnapshot.holdings.get(asset) || 0,
					netUnits: assetUnits.get(asset) || 0,
					endingUnits: finalSnapshot.holdings.get(asset) || 0,
				};
			})
			.filter(
				(asset) =>
					Math.abs(asset.startingValue) > 0.000001 ||
					Math.abs(asset.invested) > 0.000001 ||
					Math.abs(asset.endingValue) > 0.000001 ||
					Math.abs(asset.dividendIncome) > 0.000001 ||
					Math.abs(asset.endingUnits) > 0.000001
			)
			.sort((left, right) => {
				const valueDiff = Math.abs(right.endingValue) - Math.abs(left.endingValue);
				return valueDiff || left.label.localeCompare(right.label);
			});

		rows.push({
			year,
			startDate: yearStart,
			endDate: yearEnd,
			periodEnd,
			isClosed: periodEndKey === normalizeDateKey(yearEnd),
			amountInvested,
			finalWorth,
			dividendIncome,
			profitLoss: finalWorth - amountInvested + dividendIncome,
			assets,
			label:
				periodEndKey === currentDateKey && periodEnd < yearEnd
					? "Current Value"
					: `Final Value (${periodEnd.getUTCDate()} December ${periodEnd.getUTCFullYear()})`,
		});
	}

	return rows;
};

// Daily portfolio timeline from the first trade onward. Each held asset gets
// its value series plus its own trades (so markers sit on that asset's line),
// and the portfolio gets total value alongside net invested capital.
export const buildPortfolioTrend = ({ investments = [], daily = [] } = {}) => {
	const priceHistory = buildPriceHistory(daily);
	const trades = (investments || [])
		.map((investment) => ({
			asset: normalizeAsset(investment?.asset),
			dateKey: normalizeDateKey(getEffectiveTransactionDate(investment)),
			type: String(investment?.transactionType || "BUY").toUpperCase() === "SELL" ? "SELL" : "BUY",
			units: Number(investment?.units || 0),
			price: Number(investment?.value || 0),
		}))
		.filter((trade) => trade.asset && trade.dateKey)
		.sort((left, right) => (left.dateKey < right.dateKey ? -1 : left.dateKey > right.dateKey ? 1 : 0));

	if (!trades.length) {
		return { timeline: [], series: [], totalValuesByDate: [], investedByDate: [] };
	}

	// Quotes before the first trade add nothing but a flat zero run.
	const firstTradeKey = trades[0].dateKey;
	const timelineSet = new Set(trades.map((trade) => trade.dateKey));
	(daily || []).forEach((row) => {
		const dateKey = normalizeDateKey(row?.createdAt);
		if (dateKey && dateKey >= firstTradeKey) timelineSet.add(dateKey);
	});
	// ISO date keys sort correctly as strings.
	const timeline = Array.from(timelineSet).sort();
	const timelineIndex = new Map(timeline.map((dateKey, index) => [dateKey, index]));

	const tradesByAsset = new Map();
	trades.forEach((trade) => {
		const assetTrades = tradesByAsset.get(trade.asset) || [];
		assetTrades.push(trade);
		tradesByAsset.set(trade.asset, assetTrades);
	});

	const series = Array.from(tradesByAsset.entries()).map(([asset, assetTrades]) => {
		const history = priceHistory.get(asset) || [];
		const unitsByDate = [];
		const pricesByDate = [];
		const valuesByDate = [];
		// Same-day trades of one type collapse into a single marker.
		const markers = new Map();
		let units = 0;
		let lastPrice = 0;
		let historyPointer = 0;
		let tradePointer = 0;

		timeline.forEach((dateKey) => {
			while (tradePointer < assetTrades.length && assetTrades[tradePointer].dateKey <= dateKey) {
				const trade = assetTrades[tradePointer];
				units += trade.type === "SELL" ? -trade.units : trade.units;
				// Until the first quote exists, the trade price is the best mark we
				// have; without it the asset reads as worth zero on the day it's bought.
				if (historyPointer === 0) lastPrice = trade.price;

				const key = `${trade.type}:${trade.dateKey}`;
				const marker = markers.get(key) || {
					index: timelineIndex.get(trade.dateKey),
					dateKey: trade.dateKey,
					type: trade.type,
					units: 0,
					amount: 0,
				};
				marker.units += trade.units;
				marker.amount += trade.units * trade.price;
				markers.set(key, marker);
				tradePointer += 1;
			}
			while (historyPointer < history.length && history[historyPointer].date <= dateKey) {
				lastPrice = Number(history[historyPointer].price || 0);
				historyPointer += 1;
			}

			// Selling a whole position leaves float dust like 1e-14 units.
			if (Math.abs(units) < 1e-9) units = 0;
			unitsByDate.push(units);
			pricesByDate.push(lastPrice);
			// Before the first trade the asset isn't owned at all: a null leaves a
			// gap instead of piling every unbought asset onto the zero line.
			valuesByDate.push(tradePointer === 0 ? null : units * lastPrice);
		});

		return {
			asset,
			label: humanizeAssetLabel(asset),
			unitsByDate,
			pricesByDate,
			valuesByDate,
			currentValue: valuesByDate[valuesByDate.length - 1] || 0,
			trades: Array.from(markers.values())
				.map((marker) => ({ ...marker, value: valuesByDate[marker.index] || 0 }))
				.sort((left, right) => left.index - right.index),
		};
	});

	const totalValuesByDate = timeline.map((_, index) =>
		series.reduce((sum, assetSeries) => sum + (assetSeries.valuesByDate[index] || 0), 0)
	);

	// Net invested: buys add their cost, sells withdraw their proceeds.
	let invested = 0;
	let tradePointer = 0;
	const investedByDate = timeline.map((dateKey) => {
		while (tradePointer < trades.length && trades[tradePointer].dateKey <= dateKey) {
			const trade = trades[tradePointer];
			const amount = trade.units * trade.price;
			invested += trade.type === "SELL" ? -amount : amount;
			tradePointer += 1;
		}
		return invested;
	});

	return { timeline, series, totalValuesByDate, investedByDate };
};

const buildAssetByDateMap = (result = []) => {
	const data = {};
	result.forEach((item) => {
		if (item) data[normalizeAsset(item.asset)] = item;
	});
	return data;
};

export const useAssetMarketData = () => {
	const { data: assets } = useQuery("fetchAssets", fetchAssets);
	const { data: investments } = useInvestmentsQuery();
	// Shares Ladder's query rather than keying the same endpoint a second time:
	// holdings and the year ladder were each fetching /daily/assets under their own
	// key, so every load and every currency switch requested it twice.
	const { data: latestPrices, isLoading: loadingAssets } = useLatestPricesQuery();

	const assetByDate = useMemo(() => buildAssetByDateMap(latestPrices), [latestPrices]);

	const mergedAssets = useMemo(() => {
		const union = new Set((assets || []).map((asset) => normalizeAsset(asset)));
		(investments || []).forEach((investment) => union.add(normalizeAsset(investment.asset)));
		return Array.from(union).filter(Boolean).sort();
	}, [assets, investments]);

	return { assets: mergedAssets, assetByDate, loadingAssets };
};

export const useWorthAndSpent = () => {
	const { data: investments } = useInvestmentsQuery();
	const { data: daily } = useDailyQuery();

	return useMemo(() => {
		const spent = (investments || []).reduce(
			(total, investment) => total + getSignedCashFlow(investment),
			0
		);

		return {
			worth: buildPortfolioSnapshot({ investments, daily }).worth,
			spent,
		};
	}, [daily, investments]);
};
